import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, TextField, Button, CircularProgress,
  Alert, Divider, IconButton, Chip, Select, MenuItem, FormControl,
  InputLabel,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4001';

// ── types ──────────────────────────────────────────────────────────────────────
interface HourlyRow {
  id: string | null;
  reading_date: string;
  reading_hour: number;
  thermax1_nm3: number | null;
  thermax2_nm3: number | null;
  engine1_nm3: number | null;
  engine2_nm3: number | null;
  co2_biogas_nm3: number | null;
  co2_biomethane_nm3: number | null;
  co2_flare_nm3: number | null;
  biogas_flare_nm3: number | null;
  gas_inventory_nm3: number | null;
  actual_gas_stored_nm3: number | null;
  total_consumption_nm3: number;
  net_gas_production_nm3: number;
}

interface DailyRow {
  reading_date: string;
  thermax1_nm3: number | null;
  thermax2_nm3: number | null;
  engine1_nm3: number | null;
  engine2_nm3: number | null;
  co2_biogas_nm3: number | null;
  co2_biomethane_nm3: number | null;
  co2_flare_nm3: number | null;
  biogas_flare_nm3: number | null;
  gas_inventory_nm3: number | null;
  actual_gas_stored_nm3: number | null;
  total_consumption_nm3: number;
  net_gas_production_nm3: number;
}

// ── helpers ────────────────────────────────────────────────────────────────────
type EditMap = Record<number, Record<string, string>>;

const HOUR_LABELS = Array.from({ length: 24 }, (_, h) => {
  const ampm = h < 12 ? 'AM' : 'PM';
  const display = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${display}:00 ${ampm}`;
});

const fmtDate = (iso: string) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' });
};

const num = (v: string | number | null | undefined) => (v == null || v === '' || isNaN(Number(v)) ? null : Number(v));
const fmt = (v: number | null | undefined, dec = 2) =>
  v == null ? '—' : Number(v).toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

function buildYears() {
  const y = new Date().getFullYear();
  return [y - 2, y - 1, y, y + 1];
}

// ── style helpers ──────────────────────────────────────────────────────────────
const TH = (bg = '#1b5e20') => ({
  background: bg, color: '#fff', fontWeight: 700, fontSize: '0.68rem',
  textAlign: 'center' as const, px: 0.5, py: 0.8, lineHeight: 1.2,
  whiteSpace: 'nowrap' as const,
});
const TD: React.CSSProperties = {
  padding: '3px 5px', fontSize: '0.72rem', textAlign: 'center', whiteSpace: 'nowrap',
};
const TD_COMP: React.CSSProperties = {
  ...TD, fontWeight: 700, background: '#e8f5e9', color: '#1b5e20',
};
const TD_NET: React.CSSProperties = {
  ...TD, fontWeight: 700, background: '#e3f2fd', color: '#0d47a1',
};

// ── main component ─────────────────────────────────────────────────────────────
const GasMonitoring: React.FC = () => {
  const navigate = useNavigate();
  const today = new Date().toISOString().split('T')[0];
  const [tab, setTab] = useState(0);

  // ── Daily Summary state ───────────────────────────────────────────────────
  const [dsMonth, setDsMonth] = useState(new Date().getMonth() + 1); // 1-12
  const [dsYear, setDsYear]   = useState(new Date().getFullYear());
  const [dsRows, setDsRows]   = useState<DailyRow[]>([]);
  const [dsLoading, setDsLoading] = useState(false);
  const [dsError, setDsError]     = useState('');

  // ── Hourly Detail state ───────────────────────────────────────────────────
  const [hdDate, setHdDate]       = useState(today);
  const [hdRows, setHdRows]       = useState<HourlyRow[]>([]);
  const [hdEdits, setHdEdits]     = useState<EditMap>({});
  const [hdLoading, setHdLoading] = useState(false);
  const [hdSaving, setHdSaving]   = useState(false);
  const [hdError, setHdError]     = useState('');
  const [hdSuccess, setHdSuccess] = useState('');

  // ── Load Daily Summary ────────────────────────────────────────────────────
  const loadDailySummary = useCallback(async () => {
    setDsLoading(true); setDsError('');
    try {
      const ym = `${dsYear}-${String(dsMonth).padStart(2, '0')}`;
      const { data } = await axios.get(`${API}/gas-monitoring/daily-summary`, { params: { month: ym } });
      setDsRows(data);
    } catch {
      setDsError('Failed to load daily summary.');
    } finally {
      setDsLoading(false);
    }
  }, [dsMonth, dsYear]);

  useEffect(() => { if (tab === 0) loadDailySummary(); }, [tab, loadDailySummary]);

  // ── Load Hourly Detail ────────────────────────────────────────────────────
  const loadHourly = useCallback(async (date: string) => {
    setHdLoading(true); setHdError(''); setHdSuccess('');
    try {
      const { data } = await axios.get(`${API}/gas-monitoring/hourly`, { params: { date } });
      setHdRows(data);
      setHdEdits({});
    } catch {
      setHdError('Failed to load hourly data.');
    } finally {
      setHdLoading(false);
    }
  }, []);

  useEffect(() => { if (tab === 1) loadHourly(hdDate); }, [tab, hdDate, loadHourly]);

  // ── Edit helpers ──────────────────────────────────────────────────────────
  const setCell = (hour: number, field: string, val: string) =>
    setHdEdits((prev) => ({ ...prev, [hour]: { ...(prev[hour] ?? {}), [field]: val } }));

  const getCell = (hour: number, field: keyof HourlyRow): string => {
    if (hdEdits[hour]?.[field] !== undefined) return hdEdits[hour][field];
    const v = hdRows[hour]?.[field];
    return v == null ? '' : String(v);
  };

  // ── Compute totals for display ─────────────────────────────────────────────
  const computedRow = (hour: number): { total: number; net: number } => {
    const g = (f: string) => num(hdEdits[hour]?.[f] ?? hdRows[hour]?.[f as keyof HourlyRow]) ?? 0;
    const total =
      g('thermax1_nm3') + g('thermax2_nm3') +
      g('engine1_nm3')  + g('engine2_nm3')  +
      g('co2_biogas_nm3') + g('co2_biomethane_nm3') +
      g('co2_flare_nm3')  + g('biogas_flare_nm3');
    const net = total + g('gas_inventory_nm3');
    return { total, net };
  };

  // ── Save hourly ────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setHdSaving(true); setHdError(''); setHdSuccess('');
    try {
      const entries = hdRows.map((_, h) => {
        const g = (f: string) => {
          const v = hdEdits[h]?.[f] ?? (hdRows[h]?.[f as keyof HourlyRow] != null ? String(hdRows[h][f as keyof HourlyRow]) : '');
          const n2 = parseFloat(v);
          return isNaN(n2) ? undefined : n2;
        };
        return {
          readingDate: hdDate,
          readingHour: h,
          thermax1Nm3:        g('thermax1_nm3'),
          thermax2Nm3:        g('thermax2_nm3'),
          engine1Nm3:         g('engine1_nm3'),
          engine2Nm3:         g('engine2_nm3'),
          co2BiogasNm3:       g('co2_biogas_nm3'),
          co2BiomethaneNm3:   g('co2_biomethane_nm3'),
          co2FlareNm3:        g('co2_flare_nm3'),
          biogasFlareNm3:     g('biogas_flare_nm3'),
          gasInventoryNm3:    g('gas_inventory_nm3'),

        };
      }).filter((e) =>
        [e.thermax1Nm3, e.thermax2Nm3, e.engine1Nm3, e.engine2Nm3,
         e.co2BiogasNm3, e.co2BiomethaneNm3, e.co2FlareNm3, e.biogasFlareNm3,
         e.gasInventoryNm3].some((v) => v !== undefined)
      );

      if (entries.length === 0) {
        setHdError('Enter at least one value before saving.');
        return;
      }

      await axios.post(`${API}/gas-monitoring/batch`, { entries });
      setHdSuccess(`Saved ${entries.length} hour(s) for ${fmtDate(hdDate)}.`);
      loadHourly(hdDate);
    } catch {
      setHdError('Save failed. Please try again.');
    } finally {
      setHdSaving(false);
    }
  };

  // ── Delete hourly date ─────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!window.confirm(`Delete all hourly data for ${fmtDate(hdDate)}?`)) return;
    try {
      await axios.delete(`${API}/gas-monitoring/by-date`, { params: { date: hdDate } });
      loadHourly(hdDate);
    } catch {
      setHdError('Delete failed.');
    }
  };

  // ── Daily summary totals row ───────────────────────────────────────────────
  const dsTotals = dsRows.reduce(
    (acc, r) => ({
      thermax1:   acc.thermax1   + (num(r.thermax1_nm3)   ?? 0),
      thermax2:   acc.thermax2   + (num(r.thermax2_nm3)   ?? 0),
      engine1:    acc.engine1    + (num(r.engine1_nm3)    ?? 0),
      engine2:    acc.engine2    + (num(r.engine2_nm3)    ?? 0),
      co2Biogas:  acc.co2Biogas  + (num(r.co2_biogas_nm3) ?? 0),
      co2Bio:     acc.co2Bio     + (num(r.co2_biomethane_nm3) ?? 0),
      co2Flare:   acc.co2Flare   + (num(r.co2_flare_nm3)  ?? 0),
      bgFlare:    acc.bgFlare    + (num(r.biogas_flare_nm3) ?? 0),
      totalCons:  acc.totalCons  + (num(r.total_consumption_nm3) ?? 0),
      inventory:  acc.inventory  + (num(r.gas_inventory_nm3) ?? 0),
      actual:     acc.actual     + (num(r.actual_gas_stored_nm3) ?? 0),
      netGas:     acc.netGas     + (num(r.net_gas_production_nm3) ?? 0),
    }),
    { thermax1: 0, thermax2: 0, engine1: 0, engine2: 0, co2Biogas: 0, co2Bio: 0,
      co2Flare: 0, bgFlare: 0, totalCons: 0, inventory: 0, actual: 0, netGas: 0 },
  );

  // ── Hourly totals row ──────────────────────────────────────────────────────
  const hdTotals = hdRows.reduce(
    (acc, _, h) => {
      const g = (f: string) => num(hdEdits[h]?.[f] ?? hdRows[h]?.[f as keyof HourlyRow]) ?? 0;
      const { total, net } = computedRow(h);
      return {
        thermax1: acc.thermax1 + g('thermax1_nm3'),
        thermax2: acc.thermax2 + g('thermax2_nm3'),
        engine1:  acc.engine1  + g('engine1_nm3'),
        engine2:  acc.engine2  + g('engine2_nm3'),
        co2Biogas: acc.co2Biogas + g('co2_biogas_nm3'),
        co2Bio:   acc.co2Bio   + g('co2_biomethane_nm3'),
        co2Flare: acc.co2Flare + g('co2_flare_nm3'),
        bgFlare:  acc.bgFlare  + g('biogas_flare_nm3'),
        inventory: acc.inventory + g('gas_inventory_nm3'),
        actual:   acc.actual   + g('actual_gas_stored_nm3'),
        totalCons: acc.totalCons + total,
        netGas:   acc.netGas   + net,
      };
    },
    { thermax1: 0, thermax2: 0, engine1: 0, engine2: 0, co2Biogas: 0, co2Bio: 0,
      co2Flare: 0, bgFlare: 0, inventory: 0, actual: 0, totalCons: 0, netGas: 0 },
  );

  // ── Shared header ──────────────────────────────────────────────────────────
  const TableHeaderRows = ({ showDate = true }: { showDate?: boolean }) => (
    <TableHead>
      <TableRow>
        {showDate && (
          <TableCell rowSpan={2} align="center" sx={{ ...TH(), minWidth: 75, verticalAlign: 'middle' }}>
            {tab === 0 ? 'Date' : 'Time'}
          </TableCell>
        )}
        <TableCell align="center" sx={{ ...TH('#2e7d32'), minWidth: 70 }}>Thermax 1</TableCell>
        <TableCell align="center" sx={{ ...TH('#2e7d32'), minWidth: 70 }}>Thermax 2</TableCell>
        <TableCell align="center" sx={{ ...TH('#1565c0'), minWidth: 70 }}>Engine 1</TableCell>
        <TableCell align="center" sx={{ ...TH('#1565c0'), minWidth: 70 }}>Engine 2</TableCell>
        <TableCell colSpan={3} align="center" sx={{ ...TH('#6a1b9a'), minWidth: 210 }}>CO2</TableCell>
        <TableCell align="center" sx={{ ...TH('#bf360c'), minWidth: 80 }}>Biogas Flare</TableCell>
        <TableCell align="center" sx={{ ...TH('#1b5e20'), minWidth: 85 }}>Total Consumption</TableCell>
        <TableCell align="center" sx={{ ...TH('#0d47a1'), minWidth: 80 }}>Gas Inventory</TableCell>
        {tab === 0 && (
          <TableCell align="center" sx={{ ...TH('#4a148c'), minWidth: 85 }}>Actual Gas Stored</TableCell>
        )}
        <TableCell align="center" sx={{ ...TH('#004d40'), minWidth: 85 }}>Net Gas Production</TableCell>
      </TableRow>
      <TableRow>
        <TableCell align="center" sx={{ ...TH('#388e3c'), fontSize: '0.62rem' }}>Nm³</TableCell>
        <TableCell align="center" sx={{ ...TH('#388e3c'), fontSize: '0.62rem' }}>Nm³</TableCell>
        <TableCell align="center" sx={{ ...TH('#1976d2'), fontSize: '0.62rem' }}>Nm³</TableCell>
        <TableCell align="center" sx={{ ...TH('#1976d2'), fontSize: '0.62rem' }}>Nm³</TableCell>
        <TableCell align="center" sx={{ ...TH('#7b1fa2'), fontSize: '0.62rem' }}>Biogas Nm³</TableCell>
        <TableCell align="center" sx={{ ...TH('#7b1fa2'), fontSize: '0.62rem' }}>Biomethane Nm³</TableCell>
        <TableCell align="center" sx={{ ...TH('#7b1fa2'), fontSize: '0.62rem' }}>Flare Nm³</TableCell>
        <TableCell align="center" sx={{ ...TH('#e64a19'), fontSize: '0.62rem' }}>Nm³</TableCell>
        <TableCell align="center" sx={{ ...TH('#2e7d32'), fontSize: '0.62rem' }}>Nm³</TableCell>
        <TableCell align="center" sx={{ ...TH('#1565c0'), fontSize: '0.62rem' }}>Nm³</TableCell>
        {tab === 0 && (
          <TableCell align="center" sx={{ ...TH('#6a1b9a'), fontSize: '0.62rem' }}>Nm³</TableCell>
        )}
        <TableCell align="center" sx={{ ...TH('#00695c'), fontSize: '0.62rem' }}>Nm³</TableCell>
      </TableRow>
    </TableHead>
  );

  const TotalsRow = ({ t }: { t: typeof dsTotals }) => (
    <TableRow sx={{ background: '#e8f5e9', fontWeight: 700 }}>
      <TableCell align="center" sx={{ ...TD, fontWeight: 700, background: '#c8e6c9' }}>Total</TableCell>
      <TableCell align="center" sx={{ ...TD, fontWeight: 700 }}>{fmt(t.thermax1)}</TableCell>
      <TableCell align="center" sx={{ ...TD, fontWeight: 700 }}>{fmt(t.thermax2)}</TableCell>
      <TableCell align="center" sx={{ ...TD, fontWeight: 700 }}>{fmt(t.engine1)}</TableCell>
      <TableCell align="center" sx={{ ...TD, fontWeight: 700 }}>{fmt(t.engine2)}</TableCell>
      <TableCell align="center" sx={{ ...TD, fontWeight: 700 }}>{fmt(t.co2Biogas)}</TableCell>
      <TableCell align="center" sx={{ ...TD, fontWeight: 700 }}>{fmt(t.co2Bio)}</TableCell>
      <TableCell align="center" sx={{ ...TD, fontWeight: 700 }}>{fmt(t.co2Flare)}</TableCell>
      <TableCell align="center" sx={{ ...TD, fontWeight: 700 }}>{fmt(t.bgFlare)}</TableCell>
      <TableCell align="center" sx={TD_COMP}>{fmt(t.totalCons)}</TableCell>
      <TableCell align="center" sx={{ ...TD, fontWeight: 700 }}>{fmt(t.inventory)}</TableCell>
      {tab === 0 && <TableCell align="center" sx={{ ...TD, fontWeight: 700 }}>{fmt(t.actual)}</TableCell>}
      <TableCell align="center" sx={TD_NET}>{fmt(t.netGas)}</TableCell>
    </TableRow>
  );

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* Page header */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2, mb: 3, overflow: 'hidden',
          background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 60%, #388e3c 100%)',
        }}
      >
        <Box sx={{ p: 2, color: '#fff', display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <ShowChartIcon sx={{ fontSize: '2rem', opacity: 0.9 }} />
            <Box>
              <Typography variant="overline" sx={{ opacity: 0.8, lineHeight: 1, letterSpacing: 2, display: 'block' }}>
                POLOMOLOK PLANT
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Hourly Gas Monitoring
              </Typography>
            </Box>
          </Box>
          <Chip label="Nm³ units" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600 }} />
        </Box>

        {/* Tabs */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            px: 2,
            '& .MuiTab-root': { color: 'rgba(255,255,255,0.7)', fontWeight: 600, minHeight: 40 },
            '& .Mui-selected':  { color: '#fff !important' },
            '& .MuiTabs-indicator': { backgroundColor: '#a5d6a7', height: 3 },
          }}
        >
          <Tab label="Daily Summary" />
          <Tab label="Hourly Data Entry" />
        </Tabs>
      </Paper>

      {/* ── TAB 0: Daily Summary ──────────────────────────────────────────── */}
      {tab === 0 && (
        <Box>
          {/* Month/Year selector */}
          <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2, p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1b5e20' }}>
              Monthly Report
            </Typography>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Month</InputLabel>
              <Select
                value={dsMonth}
                label="Month"
                onChange={(e) => setDsMonth(Number(e.target.value))}
              >
                {MONTHS.map((m, i) => (
                  <MenuItem key={m} value={i + 1}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 90 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={dsYear}
                label="Year"
                onChange={(e) => setDsYear(Number(e.target.value))}
              >
                {buildYears().map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              size="small"
              onClick={loadDailySummary}
              disabled={dsLoading}
              sx={{ bgcolor: '#1b5e20', '&:hover': { bgcolor: '#2e7d32' } }}
            >
              {dsLoading ? <CircularProgress size={16} color="inherit" /> : 'Load'}
            </Button>
            <Typography variant="caption" sx={{ color: '#888', ml: 'auto' }}>
              Click "Hourly Data Entry" tab to enter readings per hour
            </Typography>
          </Paper>

          {dsError && <Alert severity="error" sx={{ mb: 2 }}>{dsError}</Alert>}

          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ p: 2, pb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1b5e20' }}>
                {MONTHS[dsMonth - 1]} {dsYear} — Daily Gas Summary
              </Typography>
              <Typography variant="caption" sx={{ color: '#888' }}>
                Total Consumption = Σ(Thermax1+2 + Engine1+2 + CO2+Flare) · Net Gas Production = Total Consumption + Gas Inventory
              </Typography>
            </Box>
            <Divider />
            <Box sx={{ overflowX: 'auto' }}>
              <TableContainer>
                <Table size="small" sx={{ minWidth: 1100 }}>
                  <TableHeaderRows />
                  <TableBody>
                    {dsRows.length === 0 && !dsLoading && (
                      <TableRow>
                        <TableCell colSpan={13} align="center" sx={{ py: 4, color: '#888' }}>
                          No data for {MONTHS[dsMonth - 1]} {dsYear}. Enter hourly readings to populate this summary.
                        </TableCell>
                      </TableRow>
                    )}
                    {dsLoading && (
                      <TableRow>
                        <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    )}
                    {dsRows.map((r) => (
                      <TableRow
                        key={r.reading_date}
                        hover
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f1f8e9' } }}
                        onClick={() => { setHdDate(r.reading_date); setTab(1); }}
                      >
                        <TableCell align="center" sx={{ ...TD, fontWeight: 700, bgcolor: '#f9fbe7' }}>
                          {fmtDate(r.reading_date)}
                        </TableCell>
                        <TableCell align="center" sx={TD}>{fmt(r.thermax1_nm3)}</TableCell>
                        <TableCell align="center" sx={TD}>{fmt(r.thermax2_nm3)}</TableCell>
                        <TableCell align="center" sx={TD}>{fmt(r.engine1_nm3)}</TableCell>
                        <TableCell align="center" sx={TD}>{fmt(r.engine2_nm3)}</TableCell>
                        <TableCell align="center" sx={TD}>{fmt(r.co2_biogas_nm3)}</TableCell>
                        <TableCell align="center" sx={TD}>{fmt(r.co2_biomethane_nm3)}</TableCell>
                        <TableCell align="center" sx={TD}>{fmt(r.co2_flare_nm3)}</TableCell>
                        <TableCell align="center" sx={TD}>{fmt(r.biogas_flare_nm3)}</TableCell>
                        <TableCell align="center" sx={TD_COMP}>{fmt(r.total_consumption_nm3)}</TableCell>
                        <TableCell align="center" sx={TD}>{fmt(r.gas_inventory_nm3)}</TableCell>
                        <TableCell align="center" sx={TD}>{fmt(r.actual_gas_stored_nm3)}</TableCell>
                        <TableCell align="center" sx={TD_NET}>{fmt(r.net_gas_production_nm3)}</TableCell>
                      </TableRow>
                    ))}
                    {dsRows.length > 0 && <TotalsRow t={dsTotals} />}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Paper>
        </Box>
      )}

      {/* ── TAB 1: Hourly Data Entry ──────────────────────────────────────── */}
      {tab === 1 && (
        <Box>
          {/* Date selector + actions */}
          <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2, p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1b5e20' }}>Date</Typography>
            <TextField
              type="date"
              size="small"
              value={hdDate}
              onChange={(e) => setHdDate(e.target.value)}
              sx={{ width: 180 }}
              InputLabelProps={{ shrink: true }}
            />
            <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteSweepIcon />}
                onClick={handleDelete}
                disabled={hdSaving || hdRows.every((r) => r.id === null)}
              >
                Clear Date
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={hdSaving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={hdSaving}
                sx={{ bgcolor: '#1b5e20', '&:hover': { bgcolor: '#2e7d32' } }}
              >
                {hdSaving ? 'Saving…' : 'Save All'}
              </Button>
            </Box>
          </Paper>

          {hdError   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setHdError('')}>{hdError}</Alert>}
          {hdSuccess && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setHdSuccess('')}>{hdSuccess}</Alert>}

          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ p: 2, pb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1b5e20' }}>
                Hourly Readings — {fmtDate(hdDate)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#888' }}>
                Enter values in Nm³ · Total Consumption auto-calculated · Net = Total + Gas Inventory
              </Typography>
            </Box>
            <Divider />

            {hdLoading ? (
              <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <TableContainer>
                  <Table size="small" sx={{ minWidth: 1000 }}>
                    <TableHeaderRows showDate />
                    <TableBody>
                      {Array.from({ length: 24 }, (_, h) => {
                        const { total, net } = computedRow(h);
                        const hasData = hdRows[h]?.id !== null;
                        const inp = (field: string) => (
                          <TextField
                            size="small"
                            type="number"
                            inputProps={{ step: '0.01', min: 0, style: { fontSize: '0.7rem', padding: '2px 4px', width: 68 } }}
                            value={getCell(h, field as keyof HourlyRow)}
                            onChange={(e) => setCell(h, field, e.target.value)}
                            variant="outlined"
                            sx={{ '& .MuiOutlinedInput-root': { height: 26 } }}
                          />
                        );
                        return (
                          <TableRow key={h} sx={{ bgcolor: hasData ? '#f9fbe7' : undefined }}>
                            <TableCell align="center" sx={{ ...TD, fontWeight: 700, bgcolor: hasData ? '#dcedc8' : '#f5f5f5', minWidth: 80 }}>
                              {HOUR_LABELS[h]}
                            </TableCell>
                            <TableCell align="center" sx={{ ...TD, p: '2px' }}>{inp('thermax1_nm3')}</TableCell>
                            <TableCell align="center" sx={{ ...TD, p: '2px' }}>{inp('thermax2_nm3')}</TableCell>
                            <TableCell align="center" sx={{ ...TD, p: '2px' }}>{inp('engine1_nm3')}</TableCell>
                            <TableCell align="center" sx={{ ...TD, p: '2px' }}>{inp('engine2_nm3')}</TableCell>
                            <TableCell align="center" sx={{ ...TD, p: '2px' }}>{inp('co2_biogas_nm3')}</TableCell>
                            <TableCell align="center" sx={{ ...TD, p: '2px' }}>{inp('co2_biomethane_nm3')}</TableCell>
                            <TableCell align="center" sx={{ ...TD, p: '2px' }}>{inp('co2_flare_nm3')}</TableCell>
                            <TableCell align="center" sx={{ ...TD, p: '2px' }}>{inp('biogas_flare_nm3')}</TableCell>
                            <TableCell align="center" sx={TD_COMP}>{total > 0 ? fmt(total) : '—'}</TableCell>
                            <TableCell align="center" sx={{ ...TD, p: '2px' }}>{inp('gas_inventory_nm3')}</TableCell>
                            <TableCell align="center" sx={TD_NET}>{(total > 0 || (num(getCell(h, 'gas_inventory_nm3')) ?? 0) > 0) ? fmt(net) : '—'}</TableCell>
                          </TableRow>
                        );
                      })}
                      <TotalsRow t={hdTotals} />
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default GasMonitoring;
